import { Dialog, DialogContent, DialogActions, Button, Divider, Box } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { IdLeaseAgreement } from 'realhaus-sdk';
import { RootStoreContext } from '../../../../../../global/storeContext';
import { RequestMoney } from './requestMoney';
import { RequestWizardEnd } from './requestWizardEnd';
import { SendMoney } from './sendMoney';
import { SendOrRequest } from './sendOrRequest';
import { SendWizardEnd } from './sendWizardEnd';
import { useIdentityGate } from '../../../../../../hooks/useIdentityGate';
import { CompleteAccountSetupDialog } from '../../../../../rentApplication/dialogs/completeAccountSetupDialog';
import { usePaymentAmountLimit } from '../../../../../../hooks/usePaymentAmountLimit';

const DAMAGE_SERVICE = 'Damage, missing items and unexpected cleaning';

export enum RequestWizard {
    START,
    REQUEST,
    REQUESTEND,
}

export enum SendWizard {
    START,
    SEND,
    SENDEND,
}

export enum SendRequestChoice {
    SEND = 'send',
    REQUEST = 'request',
}

interface ISendRequestPayment {
    open: boolean;
    handleCloseModal: () => void;
    onPaymentRequested: () => void;
    // Send Money creates a bill owed by the landlord; the parent opens its checkout.
    onSendMoneyBillCreated: (billId: string) => void;
    tenantName: string | undefined;
    leaseAgreement: IdLeaseAgreement;
}

export const requestPage = [RequestWizard.START, RequestWizard.REQUEST, RequestWizard.REQUESTEND];

export const sendPage = [SendWizard.START, SendWizard.SEND, SendWizard.SENDEND];

export const SendOrRequestPaymentModal: React.FC<ISendRequestPayment> = observer(
    ({
        open,
        handleCloseModal,
        tenantName,
        leaseAgreement,
        onPaymentRequested,
        onSendMoneyBillCreated,
    }) => {
        const { checkAndProceed, gateDialogProps } = useIdentityGate();
        const { exceedsLimit, limitMessage } = usePaymentAmountLimit();
        const [sendRequestChoice, setSendRequestChoice] = React.useState(SendRequestChoice.REQUEST);
        const [service, setService] = React.useState('Extra Services');
        const [amount, setAmount] = React.useState(0);
        const [sendAmount, setSendAmount] = React.useState(0);
        const [sendMoneyNote, setSendMoneyNote] = React.useState<string>('');
        const [paymentDate, setPaymentDate] = React.useState<Date>(new Date(Date.now()));
        const [attachments, setAttachments] = React.useState<File[]>([]);
        const [requestNote, setRequestNote] = React.useState<string>('');
        const [totalBillAmount, setTotalBillAmount] = React.useState(0);
        const [percentageOwing, setPercentageOwing] = React.useState(0);
        const [dateReceived, setDateReceived] = React.useState<Date>(new Date());
        const [showRequestErrors, setShowRequestErrors] = React.useState(false);
        const [showSendErrors, setShowSendErrors] = React.useState(false);
        const [files, setFiles] = React.useState<
            (File & {
                preview: string;
            })[]
        >([]);
        const firstStep =
            sendRequestChoice === SendRequestChoice.REQUEST ? requestPage[0] : sendPage[0];
        const [activeStep, setActiveStep] = React.useState(firstStep);

        const lastStep =
            sendRequestChoice === SendRequestChoice.REQUEST
                ? requestPage[requestPage.length - 1]
                : sendPage[sendPage.length - 1];

        const lastStepIndex =
            sendRequestChoice === SendRequestChoice.REQUEST
                ? requestPage.indexOf(requestPage[requestPage.length - 1])
                : sendPage.indexOf(sendPage[sendPage.length - 1]);
        const indexOfActiveStep = () =>
            sendRequestChoice === SendRequestChoice.REQUEST
                ? requestPage.indexOf(activeStep as RequestWizard)
                : sendPage.indexOf(activeStep as SendWizard);

        const { uiStore, leaseStore } = React.useContext(RootStoreContext);

        const isDamageFlow = service === DAMAGE_SERVICE;
        const computedRequestAmount = Math.round(totalBillAmount * (percentageOwing / 100) * 100) / 100;
        const effectiveAmount = isDamageFlow ? amount : computedRequestAmount;

        const handleReset = () => {
            setSendRequestChoice(SendRequestChoice.REQUEST);
            setService('Extra Services');
            setAmount(0);
            setSendAmount(0);
            setSendMoneyNote('');
            setPaymentDate(new Date(Date.now()));
            setAttachments([]);
            setRequestNote('');
            setFiles([]);
            setTotalBillAmount(0);
            setPercentageOwing(0);
            setDateReceived(new Date());
            setShowRequestErrors(false);
            setShowSendErrors(false);
            setActiveStep(firstStep);
        };

        const choiceStepComponents = () => {
            if (sendRequestChoice === SendRequestChoice.REQUEST) {
                return {
                    [RequestWizard.REQUEST]: {
                        component: (
                            <RequestMoney
                                service={service}
                                setService={setService}
                                amount={amount}
                                setAmount={setAmount}
                                paymentDate={paymentDate}
                                setPaymentDate={setPaymentDate}
                                attachments={attachments}
                                setAttachments={setAttachments}
                                setRequestNote={setRequestNote}
                                requestNote={requestNote}
                                files={files}
                                setFiles={setFiles}
                                totalBillAmount={totalBillAmount}
                                setTotalBillAmount={setTotalBillAmount}
                                percentageOwing={percentageOwing}
                                setPercentageOwing={setPercentageOwing}
                                dateReceived={dateReceived}
                                setDateReceived={setDateReceived}
                                computedAmount={computedRequestAmount}
                                showErrors={showRequestErrors}
                            />
                        ),
                        validate: () => {
                            if (exceedsLimit(effectiveAmount)) {
                                uiStore.error(limitMessage);
                                return Promise.resolve(false);
                            }
                            const isValid = isDamageFlow
                                ? !!service && !!amount && !!paymentDate && !!requestNote
                                : !!service && !!totalBillAmount && !!percentageOwing && !!paymentDate && !!requestNote;

                            if (!isValid) {
                                setShowRequestErrors(true);
                            }
                            return Promise.resolve(isValid);
                        },
                    },
                    [RequestWizard.REQUESTEND]: {
                        component: (
                            <RequestWizardEnd
                                tenantName={tenantName}
                                reason={service}
                                amount={effectiveAmount}
                                isDamageFlow={isDamageFlow}
                                totalBillAmount={totalBillAmount}
                                percentageOwing={percentageOwing}
                                dateReceived={dateReceived}
                                dateDue={paymentDate}
                                notes={requestNote}
                            />
                        ),
                        validate: () => {
                            return Promise.resolve(true);
                        },
                    },
                };
            } else
                return {
                    [SendWizard.SEND]: {
                        component: (
                            <SendMoney
                                sendAmount={sendAmount}
                                setSendMoneyAmount={setSendAmount}
                                setSendMoneyNote={setSendMoneyNote}
                                sendMoneyNote={sendMoneyNote}
                                showErrors={showSendErrors}
                            />
                        ),
                        validate: () => {
                            if (exceedsLimit(sendAmount)) {
                                uiStore.error(limitMessage);
                                return Promise.resolve(false);
                            }
                            const isValid = !!sendAmount && !!sendMoneyNote;

                            if (!isValid) {
                                setShowSendErrors(true);
                            }
                            return Promise.resolve(isValid);
                        },
                    },
                    [SendWizard.SENDEND]: {
                        component: <SendWizardEnd amount={sendAmount} sendNotes={sendMoneyNote} />,
                        validate: () => {
                            return Promise.resolve(true);
                        },
                    },
                };
        };

        //     stepComponents
        const stepComponents: {
            [key: number]: { component: JSX.Element; validate: () => Promise<boolean> };
        } = {
            [RequestWizard.START]: {
                component: (
                    <SendOrRequest
                        sendRequestChoice={sendRequestChoice}
                        setSendRequestChoice={setSendRequestChoice}
                    />
                ),
                validate: () => {
                    const isValid = !!sendRequestChoice;
                    if (!isValid) {
                        uiStore.error('Please make a choice');
                    }
                    return Promise.resolve(isValid);
                },
            },
            ...choiceStepComponents(),
        };

        const handleNext = async () => {
            const stepIndex = indexOfActiveStep();
            if (!(await stepComponents[activeStep].validate())) {
                return;
            }

            if (stepIndex === lastStepIndex) {
                // End of wizard — gate behind identity verification before money / requests fire.
                await checkAndProceed(async () => {
                    uiStore.showLoading();
                    if (sendRequestChoice === SendRequestChoice.REQUEST) {
                        const reason = isDamageFlow
                            ? `${service}: ${requestNote}`
                            : `${service}: Bill received: ${format(dateReceived, 'PP')}. ${requestNote}`;
                        await leaseStore
                            .requestPayment(
                                effectiveAmount,
                                leaseAgreement.id,
                                leaseAgreement.tenantIds[0],
                                reason,
                                paymentDate.getTime(),
                                attachments,
                            )
                            .then((state) => {
                                if (!state) {
                                    uiStore.error('Unable to request payment');
                                } else {
                                    handleCloseModal();
                                    handleReset();
                                    uiStore.success('Payment Request made');
                                    onPaymentRequested();
                                }
                                uiStore.hideLoading();
                            });
                        return;
                    }
                    // Send Money to Tenant: the server creates a bill owed by the landlord, which
                    // they pay through the usual checkout; the money is sent once that payment
                    // completes.
                    try {
                        const billId = await leaseStore.sendMoney(
                            leaseAgreement.id,
                            leaseAgreement.tenantIds[0],
                            sendAmount,
                            sendMoneyNote,
                        );
                        handleCloseModal();
                        handleReset();
                        onSendMoneyBillCreated(billId);
                    } catch (err) {
                        uiStore.error(err instanceof Error ? err.message : 'Unable to send money');
                    } finally {
                        uiStore.hideLoading();
                    }
                });
                return;
            } else {
                const step = requestPage[stepIndex + 1];
                setActiveStep(step);
            }
        };

        const handleBack = () => {
            const stepIndex = indexOfActiveStep();
            if (stepIndex === 0) {
                return;
            }
            const step = requestPage[stepIndex - 1];
            setActiveStep(step);
        };

        return (
            <>
                <Dialog
                    open={open}
                    onClose={handleCloseModal}
                    maxWidth='sm'
                    fullWidth
                    PaperProps={{ style: { padding: '1.5rem', paddingTop: 0 } }}
                >
                    <DialogContent
                        style={{
                            overflow: 'visible',
                        }}
                    >
                        {stepComponents[activeStep].component}
                    </DialogContent>
                    <Divider style={{ margin: '0 1rem', marginTop: '1rem' }} />
                    <DialogActions>
                        <Box
                            style={{
                                width: '100%',
                                padding: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            {activeStep === firstStep ? (
                                <Button
                                    onClick={() => {
                                        handleCloseModal();
                                        handleReset();
                                    }}
                                    style={{ marginRight: '1rem' }}
                                >
                                    <ChevronLeft /> Back
                                </Button>
                            ) : (
                                <Button onClick={handleBack} style={{ marginRight: '1rem' }}>
                                    <ChevronLeft /> Back
                                </Button>
                            )}

                            {activeStep === lastStep ? (
                                <Button variant='contained' color='primary' onClick={handleNext}>
                                    {sendRequestChoice === SendRequestChoice.REQUEST
                                        ? 'Confirm and request'
                                        : 'Confirm and pay'}
                                </Button>
                            ) : (
                                <Button variant='contained' color='primary' onClick={handleNext}>
                                    Next
                                </Button>
                            )}
                        </Box>
                    </DialogActions>
                </Dialog>
                <CompleteAccountSetupDialog {...gateDialogProps} />
            </>
        );
    },
);
